// Self-service account page (online-only). View/edit own profile + preferences, change own
// email (re-verifies current password) and password. Admin-governed fields are shown read-only.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchMe, patchProfile, patchEmail, postChangePassword, verifyStepUpPassword, type AccountMe } from '../lib/account';
import { canAccessRoute, defaultHomeRoute } from '@avsuite/shared';
import AppHeader from '../components/AppHeader';
import { StepUpFields } from '../components/admin/StepUp';
import { getProfile, changePin, resetPin } from '../lib/offline/profiles';

// Navigable routes offerable as a home page, with friendly labels (gated per role below).
const HOME_ROUTE_LABELS: Record<string, string> = {
  '/profile': 'Profile',
  '/ops-board': 'Ops Board — Flight School',
  '/admin': 'Admin',
  '/admin/orphaned': 'Orphaned bookings',
};

type Banner = { tone: 'ok' | 'err'; text: string } | null;

export default function Profile() {
  const navigate = useNavigate();
  const { applyUserUpdate, logout } = useAuth();
  const [me, setMe] = useState<AccountMe | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    void fetchMe().then((r) => { if (r.error) setLoadErr(r.error); else setMe(r.data); });
  }, []);

  if (loadErr) return <Shell><Banner b={{ tone: 'err', text: loadErr }} /></Shell>;
  if (!me) return <Shell><p style={S.dim}>Loading…</p></Shell>;

  return (
    <Shell onBack={() => navigate('/')}>
      <ProfileSection me={me} setMe={setMe} applyUserUpdate={applyUserUpdate} />
      <HomeSection me={me} setMe={setMe} applyUserUpdate={applyUserUpdate} />
      <PreferencesSection me={me} setMe={setMe} applyUserUpdate={applyUserUpdate} />
      <EmailSection me={me} setMe={setMe} applyUserUpdate={applyUserUpdate} />
      <PasswordSection onChanged={async () => { await logout(); navigate('/login', { replace: true }); }} />
      <PinSection />
      <ReadOnlySection me={me} />
    </Shell>
  );
}

// ── Profile (name / preferred name / mobile) ─────────────────────────────────
function ProfileSection({ me, setMe, applyUserUpdate }: SectionProps) {
  const [full_name, setFull] = useState(me.full_name);
  const [first_name, setFirst] = useState(me.first_name ?? '');
  const [last_name, setLast] = useState(me.last_name ?? '');
  const [company, setCompany] = useState(me.company ?? '');
  const [preferred_name, setPref] = useState(me.preferred_name ?? '');
  const [mobile, setMobile] = useState(me.mobile ?? '');
  const [banner, setBanner] = useState<Banner>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBanner(null);
    const f = first_name.trim(), l = last_name.trim(), c = company.trim();
    // Phase 3: if the structured name is in use, the server DERIVES full_name from it; otherwise
    // the direct full_name edit stands (back-compat / staff). Only send the structured triple when
    // it's actually populated, so a full_name-only edit isn't forced through the (first+last)|company rule.
    const usingStructured = !!(f || l || c);
    if (usingStructured && !((f && l) || c)) { setBanner({ tone: 'err', text: 'Enter a first and last name, or a company.' }); return; }
    setBusy(true);
    const r = await patchProfile(
      usingStructured
        ? { first_name: f || null, last_name: l || null, company: c || null, preferred_name: preferred_name.trim() || null, mobile: mobile.trim() || null }
        : { full_name: full_name.trim(), preferred_name: preferred_name.trim() || null, mobile: mobile.trim() || null },
    );
    setBusy(false);
    if (r.error || !r.data) { setBanner({ tone: 'err', text: r.error ?? 'Could not save.' }); return; }
    setMe(r.data);
    setFull(r.data.full_name);
    applyUserUpdate({ full_name: r.data.full_name, preferred_name: r.data.preferred_name });
    setBanner({ tone: 'ok', text: 'Profile saved.' });
  }

  return (
    <Card title="Profile">
      <Field label="First name"><input style={S.input} value={first_name} onChange={(e) => setFirst(e.target.value)} placeholder="(optional)" /></Field>
      <Field label="Last name"><input style={S.input} value={last_name} onChange={(e) => setLast(e.target.value)} placeholder="(optional)" /></Field>
      <Field label="Company"><input style={S.input} value={company} onChange={(e) => setCompany(e.target.value)} placeholder="(optional)" /></Field>
      <Field label="Full name"><input style={S.input} value={full_name} onChange={(e) => setFull(e.target.value)} /></Field>
      <p style={S.note}>If you set a first and last name (or a company), your display name is derived from them.</p>
      <Field label="Preferred name"><input style={S.input} value={preferred_name} onChange={(e) => setPref(e.target.value)} placeholder="(optional)" /></Field>
      <Field label="Mobile"><input style={S.input} value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="(optional)" /></Field>
      <Banner b={banner} />
      <button style={S.primary} disabled={busy} onClick={save}>Save profile</button>
    </Card>
  );
}

// ── Home page (global nav landing) ───────────────────────────────────────────
function HomeSection({ me, setMe, applyUserUpdate }: SectionProps) {
  const options = Object.keys(HOME_ROUTE_LABELS).filter((p) => canAccessRoute(p, me.roles));
  const defLabel = HOME_ROUTE_LABELS[defaultHomeRoute(me.roles)] ?? 'role default';
  const [val, setVal] = useState(me.home_route ?? '');
  const [banner, setBanner] = useState<Banner>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true); setBanner(null);
    const r = await patchProfile({ home_route: val ? val : null });
    setBusy(false);
    if (r.error || !r.data) { setBanner({ tone: 'err', text: r.error ?? 'Could not save.' }); return; }
    setMe(r.data);
    applyUserUpdate({ home_route: r.data.home_route });
    setVal(r.data.home_route ?? '');
    setBanner({ tone: 'ok', text: 'Home page saved.' });
  }

  return (
    <Card title="Home page">
      <p style={S.note}>Where the Home button and login take you. “Default for my role” follows your role automatically.</p>
      <Field label="Home page">
        <select style={S.input} value={val} onChange={(e) => setVal(e.target.value)}>
          <option value="">Default for my role ({defLabel})</option>
          {options.map((p) => <option key={p} value={p}>{HOME_ROUTE_LABELS[p]}</option>)}
        </select>
      </Field>
      <Banner b={banner} />
      <button style={S.primary} disabled={busy} onClick={save}>Save home page</button>
    </Card>
  );
}

// ── Preferences (display + notifications) ────────────────────────────────────
function PreferencesSection({ me, setMe, applyUserUpdate }: SectionProps) {
  const [f, setF] = useState({
    pref_theme: me.pref_theme, pref_dark_accent: me.pref_dark_accent, pref_font_size: me.pref_font_size, pref_time_entry: me.pref_time_entry,
    notif_roster_sms: me.notif_roster_sms, notif_roster_email: me.notif_roster_email,
    notif_quiet_start: me.notif_quiet_start ?? '', notif_quiet_end: me.notif_quiet_end ?? '',
  });
  const [banner, setBanner] = useState<Banner>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: unknown) => setF((p) => ({ ...p, [k]: v }));

  async function save() {
    setBusy(true); setBanner(null);
    const r = await patchProfile({
      pref_theme: f.pref_theme, pref_dark_accent: f.pref_dark_accent, pref_font_size: f.pref_font_size, pref_time_entry: f.pref_time_entry,
      notif_roster_sms: f.notif_roster_sms, notif_roster_email: f.notif_roster_email,
      notif_quiet_start: f.notif_quiet_start || null, notif_quiet_end: f.notif_quiet_end || null,
    });
    setBusy(false);
    if (r.error || !r.data) { setBanner({ tone: 'err', text: r.error ?? 'Could not save.' }); return; }
    setMe(r.data);
    applyUserUpdate({ pref_theme: r.data.pref_theme, pref_dark_accent: r.data.pref_dark_accent, pref_font_size: r.data.pref_font_size, pref_time_entry: r.data.pref_time_entry });
    setBanner({ tone: 'ok', text: 'Preferences saved.' });
  }

  return (
    <Card title="Preferences">
      <Field label="Theme"><Select value={f.pref_theme} onChange={(v) => set('pref_theme', v)} opts={['light', 'dark', 'auto']} /></Field>
      <Field label="Dark accent"><Select value={f.pref_dark_accent} onChange={(v) => set('pref_dark_accent', v)} opts={['blue', 'amber', 'red']} /></Field>
      <Field label="Font size"><Select value={f.pref_font_size} onChange={(v) => set('pref_font_size', v)} opts={['standard', 'large']} /></Field>
      <Field label="Time entry"><Select value={f.pref_time_entry} onChange={(v) => set('pref_time_entry', v)} opts={['picker', 'text', 'keypad']} /></Field>
      <Toggle label="Roster SMS" checked={f.notif_roster_sms} onChange={(v) => set('notif_roster_sms', v)} />
      <Toggle label="Roster email" checked={f.notif_roster_email} onChange={(v) => set('notif_roster_email', v)} />
      <Field label="Quiet hours start"><input type="time" style={S.input} value={f.notif_quiet_start} onChange={(e) => set('notif_quiet_start', e.target.value)} /></Field>
      <Field label="Quiet hours end"><input type="time" style={S.input} value={f.notif_quiet_end} onChange={(e) => set('notif_quiet_end', e.target.value)} /></Field>
      <Banner b={banner} />
      <button style={S.primary} disabled={busy} onClick={save}>Save preferences</button>
    </Card>
  );
}

// ── Email (identity-sensitive) ───────────────────────────────────────────────
function EmailSection({ me, setMe, applyUserUpdate }: SectionProps) {
  const [new_email, setEmail] = useState(me.email);
  const [current_password, setPw] = useState('');
  const [banner, setBanner] = useState<Banner>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true); setBanner(null);
    const r = await patchEmail(new_email.trim(), current_password);
    setBusy(false);
    if (r.error || !r.data) { setBanner({ tone: 'err', text: r.error ?? 'Could not change email.' }); return; }
    setMe(r.data); setPw('');
    applyUserUpdate({ email: r.data.email });
    setBanner({ tone: 'ok', text: 'Email updated.' });
  }

  return (
    <Card title="Login email">
      <p style={S.note}>This is your login identifier. Enter it carefully — it isn't verified by email yet.</p>
      <Field label="New email"><input style={S.input} value={new_email} onChange={(e) => setEmail(e.target.value)} /></Field>
      <Field label="Current password"><input type="password" style={S.input} value={current_password} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" /></Field>
      <Banner b={banner} />
      <button style={S.primary} disabled={busy || !current_password} onClick={save}>Update email</button>
    </Card>
  );
}

// ── Password ─────────────────────────────────────────────────────────────────
function PasswordSection({ onChanged }: { onChanged: () => void }) {
  const [current_password, setCur] = useState('');
  const [new_password, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [banner, setBanner] = useState<Banner>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBanner(null);
    if (new_password !== confirm) { setBanner({ tone: 'err', text: 'New password and confirmation do not match.' }); return; }
    if (new_password.length < 12) { setBanner({ tone: 'err', text: 'New password must be at least 12 characters.' }); return; }
    setBusy(true);
    const r = await postChangePassword(current_password, new_password);
    setBusy(false);
    if (r.error || !r.data) { setBanner({ tone: 'err', text: r.error ?? 'Could not change password.' }); return; }
    setBanner({ tone: 'ok', text: 'Password changed — signing you out…' });
    setTimeout(onChanged, 900); // all sessions revoked → re-login required
  }

  return (
    <Card title="Password">
      <p style={S.note}>Changing your password signs you out of all devices.</p>
      <Field label="Current password"><input type="password" style={S.input} value={current_password} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" /></Field>
      <Field label="New password"><input type="password" style={S.input} value={new_password} onChange={(e) => setNew(e.target.value)} autoComplete="new-password" placeholder="At least 12 characters" /></Field>
      <Field label="Confirm new password"><input type="password" style={S.input} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" /></Field>
      <Banner b={banner} />
      <button style={S.primary} disabled={busy || !current_password || !new_password} onClick={save}>Change password</button>
    </Card>
  );
}

// ── Device PIN (PR-000042 light interim) ─────────────────────────────────────
// Change (prove current PIN) or reset (prove account password via step-up) the PIN on THIS
// device. Both re-wrap the in-memory/unwrapped device key under the new PIN, so the cached
// data survives untouched. The PIN stays local per device (never sent to the server) — the
// copy below is deliberately honest about that scope. Server-verified account PIN with real
// cross-device reset is the pre-launch item (docs/future-features-account-pin.md).

// Autofill-proof numeric PIN input (the OfflineAuthGate pattern, PR-000026): readOnly until
// armed by a pointer-down or a first digit, one-time-code autocomplete, manager-ignore flags,
// digits only, distinct name/id per field.
function PinInput({ label, value, onChange, name }: { label: string; value: string; onChange: (v: string) => void; name: string }) {
  const [armed, setArmed] = useState(false);
  return (
    <Field label={label}>
      <input
        type="password" inputMode="numeric" pattern="\d*" maxLength={12}
        name={name} id={name} autoComplete="one-time-code"
        data-1p-ignore="true" data-lpignore="true" data-bwignore="true"
        readOnly={!armed} onPointerDown={() => setArmed(true)}
        onKeyDown={(e) => {
          // readOnly swallows the arming keystroke, so apply the first digit by hand.
          if (!armed && /^\d$/.test(e.key)) { e.preventDefault(); setArmed(true); onChange((value + e.key).slice(0, 12)); }
        }}
        value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
        style={S.input}
      />
    </Field>
  );
}

function PinSection() {
  const { user } = useAuth(); // profile keys are per user id (the auth session's user)
  const userId = user?.id ?? null;
  const [hasProfile, setHasProfile] = useState<boolean | null>(null);

  // Change form
  const [curPin, setCurPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [newPin2, setNewPin2] = useState('');
  const [changeBanner, setChangeBanner] = useState<Banner>(null);
  const [changeBusy, setChangeBusy] = useState(false);

  // Reset form
  const [resetPw, setResetPw] = useState('');
  const [resetPin1, setResetPin1] = useState('');
  const [resetPin2, setResetPin2] = useState('');
  const [resetBanner, setResetBanner] = useState<Banner>(null);
  const [resetBusy, setResetBusy] = useState(false);

  useEffect(() => {
    if (!userId) return;
    void getProfile(userId).then((p) => setHasProfile(!!p));
  }, [userId]);

  async function doChange() {
    setChangeBanner(null);
    if (!userId) return;
    if (newPin !== newPin2) { setChangeBanner({ tone: 'err', text: 'New PIN and confirmation do not match.' }); return; }
    setChangeBusy(true);
    const r = await changePin(userId, curPin, newPin);
    setChangeBusy(false);
    if (!r.ok) { setChangeBanner({ tone: 'err', text: r.error }); return; }
    setCurPin(''); setNewPin(''); setNewPin2('');
    setChangeBanner({ tone: 'ok', text: 'PIN changed on this device. Your saved data stays.' });
  }

  async function doReset() {
    setResetBanner(null);
    if (!userId) return;
    if (resetPin1 !== resetPin2) { setResetBanner({ tone: 'err', text: 'New PIN and confirmation do not match.' }); return; }
    setResetBusy(true);
    // Server-side password check first (shared step-up machinery, rate-limited, no side effects).
    const v = await verifyStepUpPassword(resetPw);
    if (v.error || !v.data?.verified) {
      setResetBusy(false);
      setResetBanner({ tone: 'err', text: v.error ?? 'Password is incorrect.' });
      return;
    }
    const r = await resetPin(userId, resetPin1);
    setResetBusy(false);
    if (!r.ok) { setResetBanner({ tone: 'err', text: r.error }); return; }
    setResetPw(''); setResetPin1(''); setResetPin2('');
    setResetBanner({ tone: 'ok', text: 'PIN reset on this device. Your saved data stays.' });
  }

  return (
    <Card title="Device PIN">
      <p style={S.note}>
        Your PIN unlocks AvSuite on this device when you are offline. Changing it here updates the
        PIN on this device. Your saved data stays.
      </p>
      {hasProfile === false && (
        <p style={S.note}>No PIN is set up on this device yet. You will be asked to set one the next time this device is enrolled.</p>
      )}
      {hasProfile && (
        <>
          <h4 style={S.subTitle}>Change PIN</h4>
          <PinInput label="Current PIN" value={curPin} onChange={setCurPin} name="pin-current" />
          <PinInput label="New PIN (at least 6 digits)" value={newPin} onChange={setNewPin} name="pin-new" />
          <PinInput label="Confirm new PIN" value={newPin2} onChange={setNewPin2} name="pin-confirm" />
          <Banner b={changeBanner} />
          <button style={S.primary} disabled={changeBusy || !curPin || !newPin} onClick={doChange}>Change PIN</button>

          <h4 style={{ ...S.subTitle, marginTop: 22 }}>Forgot your PIN?</h4>
          <p style={S.note}>Confirm your account password instead, then choose a new PIN.</p>
          {/* StepUpFields is styled for light dialog surfaces — give it a light chip on this dark card. */}
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '2px 12px 12px', marginBottom: 12 }}>
            <StepUpFields password={resetPw} setPassword={setResetPw} notice="Confirm with your account password to reset the PIN on this device." />
          </div>
          <PinInput label="New PIN (at least 6 digits)" value={resetPin1} onChange={setResetPin1} name="pin-reset-new" />
          <PinInput label="Confirm new PIN" value={resetPin2} onChange={setResetPin2} name="pin-reset-confirm" />
          <Banner b={resetBanner} />
          <button style={S.primary} disabled={resetBusy || !resetPw || !resetPin1} onClick={doReset}>Reset PIN</button>
        </>
      )}
    </Card>
  );
}

// ── Read-only (admin-governed) ───────────────────────────────────────────────
function ReadOnlySection({ me }: { me: AccountMe }) {
  return (
    <Card title="Managed by your administrator">
      <Row label="Pilot code" value={me.pilot_code ?? '—'} />
      <Row label="Roles" value={me.roles.join(' · ') || '—'} />
      <Row label="Home base" value={me.home_base_id ?? '—'} />
      <Row label="Last login" value={me.last_login_at ? new Date(me.last_login_at).toLocaleString() : '—'} />
      <p style={S.note}>Contact your administrator to change these.</p>
    </Card>
  );
}

// ── Small UI atoms ────────────────────────────────────────────────────────────
interface SectionProps { me: AccountMe; setMe: (m: AccountMe) => void; applyUserUpdate: ReturnType<typeof useAuth>['applyUserUpdate']; }

function Shell({ children, onBack }: { children: React.ReactNode; onBack?: () => void }) {
  return (
    <div style={S.page}>
      <AppHeader context="Account">
        {onBack && <button style={S.ghost} onClick={onBack}>← Back</button>}
      </AppHeader>
      <div style={S.body}>{children}</div>
    </div>
  );
}
function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return <div style={S.card}><h3 style={S.cardTitle}>{title}</h3>{children}</div>;
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={S.field}><span style={S.label}>{label}</span>{children}</label>;
}
function Row({ label, value }: { label: string; value: string }) {
  return <div style={S.row}><span style={S.label}>{label}</span><span style={S.roVal}>{value}</span></div>;
}
function Select({ value, onChange, opts }: { value: string; onChange: (v: string) => void; opts: string[] }) {
  return <select style={S.input} value={value} onChange={(e) => onChange(e.target.value)}>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
}
function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return <label style={{ ...S.field, flexDirection: 'row', alignItems: 'center', gap: 10 }}><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /><span style={S.label}>{label}</span></label>;
}
function Banner({ b }: { b: Banner }) {
  if (!b) return null;
  return <div style={{ ...S.banner, ...(b.tone === 'ok' ? S.bannerOk : S.bannerErr) }}>{b.text}</div>;
}

const S: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: '#0f172a', color: '#f1f5f9', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', height: 56, background: '#1e293b', borderBottom: '1px solid #334155' },
  brand: { fontSize: 17, fontWeight: 700, color: '#f1f5f9', letterSpacing: -0.3 },
  ghost: { padding: '6px 14px', background: 'transparent', border: '1px solid #334155', borderRadius: 6, color: '#94a3b8', fontSize: 13, cursor: 'pointer' },
  body: { padding: '32px 24px', maxWidth: 560, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 },
  card: { background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 20 },
  cardTitle: { margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#f1f5f9' },
  subTitle: { margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#cbd5e1' },
  field: { display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 },
  label: { fontSize: 12, fontWeight: 600, color: '#94a3b8' },
  input: { padding: '8px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', fontSize: 14, fontFamily: 'inherit' },
  primary: { marginTop: 4, padding: '8px 16px', background: '#1d4ed8', border: 'none', borderRadius: 6, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  note: { margin: '0 0 12px', fontSize: 12, color: '#64748b' },
  row: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #243044' },
  roVal: { fontSize: 13, color: '#cbd5e1' },
  banner: { margin: '8px 0', padding: '8px 12px', borderRadius: 6, fontSize: 13 },
  bannerOk: { background: '#064e3b', color: '#a7f3d0', border: '1px solid #065f46' },
  bannerErr: { background: '#4c1d1d', color: '#fecaca', border: '1px solid #7f1d1d' },
  dim: { color: '#64748b' },
};
